import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import PuneMapModal from '../common/PuneMapModal';

interface Location {
  _id: string;
  srNo: string;
  location: string;
  ca: string;
  area: string;
  stretchSide: string;
  from: string;
  fromLatLong: string;
  to: string;
  toLatLong: string;
  minPlotFrontageMeters: string;
  roadType: string;
  minPlotDepthMeters: string;
  district: string;
  state: string;
  stretchSrNoArea: string;
  areaStretch: string;
}

// Generate Google Maps directions URL
const getGoogleMapsUrl = (fromLatLong: string, toLatLong: string): string | null => {
  try {
    const parseCoords = (coords: string): [number, number] | null => {
      if (!coords || coords === '-') return null;
      const parts = coords.split(',').map(s => parseFloat(s.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return [parts[0], parts[1]];
      }
      return null;
    };

    const from = parseCoords(fromLatLong);
    const to = parseCoords(toLatLong);
    
    if (!from || !to) return null;
    
    return `https://www.google.com/maps/dir/${from[0]},${from[1]}/${to[0]},${to[1]}`;
  } catch {
    return null;
  }
};

// Haversine formula to calculate distance between two lat/long points
const calculateDistance = (fromLatLong: string, toLatLong: string): string => {
  try {
    const parseCoords = (coords: string): [number, number] | null => {
      if (!coords || coords === '-') return null;
      const parts = coords.split(',').map(s => parseFloat(s.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return [parts[0], parts[1]];
      }
      return null;
    };

    const from = parseCoords(fromLatLong);
    const to = parseCoords(toLatLong);
    
    if (!from || !to) return '-';

    const R = 6371; // Earth's radius in km
    const dLat = (to[0] - from[0]) * Math.PI / 180;
    const dLon = (to[1] - from[1]) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(from[0] * Math.PI / 180) * Math.cos(to[0] * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    if (distance < 1) {
      return `${(distance * 1000).toFixed(0)}m`;
    }
    return `${distance.toFixed(2)}km`;
  } catch {
    return '-';
  }
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  padding: 16px 12px;
  @media (max-width: 480px) {
    padding: 10px 6px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
  position: relative;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const Title = styled.h1`
  color: white;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0 0 4px 0;
  @media (max-width: 480px) {
    font-size: 1.15rem;
    text-align: center;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  margin: 0;
`;

const HeaderToggleWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  @media (max-width: 600px) {
    position: static;
    transform: none;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  padding: 10px 14px;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 8px 10px;
  }
`;

const StatsGroup = styled.div`
  display: flex;
  gap: 16px;
  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const StatItem = styled.div`
  color: white;
  text-align: center;
`;

const StatNumber = styled.span`
  font-size: 1.3rem;
  font-weight: 700;
  display: block;
`;

const StatLabel = styled.span`
  font-size: 0.7rem;
  opacity: 0.85;
`;

const SearchGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: nowrap;
  @media (max-width: 600px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const SearchInput = styled.input`
  padding: 8px 14px;
  border: none;
  border-radius: 20px;
  font-size: 0.85rem;
  width: 220px;
  background: white;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255,255,255,0.3);
  }
  @media (max-width: 600px) {
    width: 100%;
    min-width: 0;
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: none;
  border-radius: 20px;
  font-size: 0.85rem;
  background: white;
  cursor: pointer;
  &:focus {
    outline: none;
  }
  @media (max-width: 600px) {
    flex: 1;
    min-width: 0;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 10px;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
`;

const SrNoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SrNo = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
  color: #1e3a5f;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #6b7280;
  display: flex;
  align-items: center;
  transition: color 0.2s;
  &:hover {
    color: #1e3a5f;
  }
`;

const LocationName = styled.span`
  background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BadgesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const Badge = styled.span<{ color?: string }>`
  background: ${props => props.color || '#e5e7eb'};
  color: ${props => props.color ? 'white' : '#374151'};
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
`;

const StretchBadge = styled.span<{ side: string }>`
  background: ${props => props.side === 'LHS' ? '#10b981' : props.side === 'RHS' ? '#f59e0b' : '#6b7280'};
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
`;

const RouteSection = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  background: #f8fafc;
  border-radius: 8px;
  padding: 8px;
`;

const RoutePoint = styled.div`
  flex: 1;
  min-width: 0;
`;

const RouteLabel = styled.div`
  font-size: 0.65rem;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const RouteName = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2937;
  word-wrap: break-word;
  line-height: 1.2;
`;

const RouteCoords = styled.div`
  font-family: monospace;
  font-size: 0.65rem;
  color: #6b7280;
  margin-top: 2px;
`;

const RouteConnector = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  position: relative;
`;

const ConnectorLine = styled.div`
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  position: relative;
  &::before, &::after {
    content: '';
    position: absolute;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    top: -2px;
  }
  &::before {
    left: -3px;
    background: #10b981;
  }
  &::after {
    right: -3px;
    background: #3b82f6;
  }
`;

const DistanceLabel = styled.a`
  font-size: 0.7rem;
  font-weight: 700;
  color: #1e3a5f;
  margin-top: 4px;
  background: #e0f2fe;
  padding: 1px 6px;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #bae6fd;
    transform: scale(1.05);
  }
`;

const SpecsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const SpecItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpecLabel = styled.span`
  font-size: 0.6rem;
  color: #9ca3af;
  text-transform: uppercase;
`;

const SpecValue = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2937;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: white;
  font-size: 1.1rem;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: rgba(255,255,255,0.8);
  font-size: 1rem;
  background: rgba(255,255,255,0.1);
  border-radius: 10px;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.75rem;
`;

const ViewToggleGroup = styled.div`
  display: flex;
  gap: 4px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 3px;
`;

const ViewToggleBtn = styled.button<{ $active?: boolean }>`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#1e3a5f' : 'white'};
  &:hover {
    background: ${props => props.$active ? 'white' : 'rgba(255,255,255,0.25)'};
  }
`;

const DownloadBtn = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  background: #10b981;
  color: white;
  transition: all 0.2s;
  &:hover {
    background: #059669;
    transform: translateY(-1px);
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  @media (max-width: 600px) {
    border-radius: 6px;
  }
`;

const ExcelTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
`;

const TableHeader = styled.thead`
  background: #1e3a5f;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Th = styled.th`
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  font-size: 0.7rem;
  white-space: nowrap;
  border-right: 1px solid rgba(255,255,255,0.2);
  &:last-child {
    border-right: none;
  }
`;

const TableBody = styled.tbody`
  tr:nth-child(even) {
    background: #f8fafc;
  }
  tr:hover {
    background: #e0f2fe;
  }
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  vertical-align: top;
  &:last-child {
    border-right: none;
  }
`;

const TableScrollContainer = styled.div`
  max-height: calc(100vh - 220px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 4px 8px;
  @media (max-width: 600px) {
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const PageButton = styled.button<{ isActive?: boolean }>`
  min-width: 28px;
  height: 28px;
  border: none;
  border-radius: 14px;
  background: ${props => props.isActive ? 'white' : 'transparent'};
  color: ${props => props.isActive ? '#1e3a5f' : 'white'};
  font-size: 0.75rem;
  font-weight: ${props => props.isActive ? '700' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover:not(:disabled) {
    background: ${props => props.isActive ? 'white' : 'rgba(255,255,255,0.25)'};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.span`
  color: white;
  font-size: 0.7rem;
  padding: 0 6px;
  opacity: 0.9;
`;

const PerPageSelect = styled.select`
  padding: 4px 8px;
  border: none;
  border-radius: 12px;
  font-size: 0.75rem;
  background: white;
  color: #1e3a5f;
  cursor: pointer;
  &:focus {
    outline: none;
  }
`;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

const AreaStretchInfo: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [roadTypeFilter, setRoadTypeFilter] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('table');
  const [showPuneMapModal, setShowPuneMapModal] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    filterLocations();
    setCurrentPage(1); // Reset to page 1 when filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, searchTerm, areaFilter, roadTypeFilter]);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      if (response.data.success) {
        setLocations(response.data.locations);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterLocations = () => {
    let filtered = [...locations];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(loc => 
        loc.location?.toLowerCase().includes(term) ||
        loc.from?.toLowerCase().includes(term) ||
        loc.to?.toLowerCase().includes(term) ||
        loc.area?.toLowerCase().includes(term) ||
        loc.ca?.toLowerCase().includes(term) ||
        loc.srNo?.toLowerCase().includes(term)
      );
    }

    if (areaFilter) {
      filtered = filtered.filter(loc => loc.area === areaFilter);
    }

    if (roadTypeFilter) {
      filtered = filtered.filter(loc => loc.roadType === roadTypeFilter);
    }

    // Sort numerically by Sr.No
    filtered.sort((a, b) => {
      const numA = parseInt(a.srNo || '0', 10);
      const numB = parseInt(b.srNo || '0', 10);
      return numA - numB;
    });

    setFilteredLocations(filtered);
  };

  const handleCopySrNo = (srNo: string) => {
    navigator.clipboard.writeText(srNo).then(() => {
      setCopiedId(srNo);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const uniqueAreas = Array.from(new Set(locations.map(loc => loc.area).filter(Boolean))).sort();
  const uniqueRoadTypes = Array.from(new Set(locations.map(loc => loc.roadType).filter(Boolean))).sort();

  // Pagination calculations
  const totalPages = Math.ceil(filteredLocations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLocations = filteredLocations.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    return pages;
  };

  const handleDownloadExcel = () => {
    const dataToExport = filteredLocations.map((loc, idx) => ({
      'Sr.No': loc.srNo || idx + 1,
      'Location': loc.location || '-',
      'CA': loc.ca || '-',
      'Area': loc.area || '-',
      'Stretch Side': loc.stretchSide || '-',
      'From': loc.from || '-',
      'From Lat/Long': loc.fromLatLong || '-',
      'To': loc.to || '-',
      'To Lat/Long': loc.toLatLong || '-',
      'Distance': calculateDistance(loc.fromLatLong, loc.toLatLong),
      'Road Type': loc.roadType || '-',
      'Min Frontage (m)': loc.minPlotFrontageMeters || '-',
      'Min Depth (m)': loc.minPlotDepthMeters || '-',
      'District': loc.district || '-',
      'State': loc.state || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Area Stretch Info');
    
    // Auto-size columns
    const colWidths = Object.keys(dataToExport[0] || {}).map(key => ({ wch: Math.max(key.length + 2, 15) }));
    worksheet['!cols'] = colWidths;
    
    XLSX.writeFile(workbook, 'Area_Stretch_Information.xlsx');
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <div>Loading locations...</div>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Title>🗺️ Advertised Location Information</Title>
          <HeaderRow>
            <Subtitle>Available CNG station locations and specifications</Subtitle>
            <HeaderToggleWrapper>
              <ViewToggleGroup>
                <ViewToggleBtn $active={viewMode === 'cards'} onClick={() => setViewMode('cards')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  Cards
                </ViewToggleBtn>
                <ViewToggleBtn $active={viewMode === 'table'} onClick={() => setViewMode('table')}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                  Table
                </ViewToggleBtn>
              </ViewToggleGroup>
            </HeaderToggleWrapper>
          </HeaderRow>
        </Header>

        {/* Prominent View PUNE GA MAP box */}
        <div
          onClick={() => setShowPuneMapModal(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 22px',
            marginBottom: '12px',
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(220, 38, 38, 0.4), 0 0 0 2px rgba(255,255,255,0.15)',
            transition: 'all 0.25s ease',
            border: '2px solid rgba(255,255,255,0.25)',
            animation: 'mapBoxPulse 2.5s ease-in-out infinite',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(220,38,38,0.5), 0 0 0 2px rgba(255,255,255,0.3)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0) scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(220,38,38,0.4), 0 0 0 2px rgba(255,255,255,0.15)'; }}
        >
          <style>{`
            @keyframes mapBoxPulse {
              0%, 100% { box-shadow: 0 4px 20px rgba(220,38,38,0.4), 0 0 0 2px rgba(255,255,255,0.15); }
              50% { box-shadow: 0 4px 28px rgba(220,38,38,0.6), 0 0 0 4px rgba(255,255,255,0.25); }
            }
          `}</style>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <div>
            <div style={{ color: '#fff', fontSize: '15px', fontWeight: 800, letterSpacing: '0.5px', lineHeight: 1.2 }}>
              📍 View PUNE GA MAP
            </div>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px', fontWeight: 500 }}>
              Click to view advertised locations on map
            </div>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </div>

        <TopBar>
          <StatsGroup>
            <StatItem>
              <StatNumber>{locations.length}</StatNumber>
              <StatLabel>Total</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{uniqueAreas.length}</StatNumber>
              <StatLabel>Areas</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{filteredLocations.length}</StatNumber>
              <StatLabel>Showing</StatLabel>
            </StatItem>
          </StatsGroup>
          <SearchGroup>
            <SearchInput
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FilterSelect value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
              <option value="">All Areas</option>
              {uniqueAreas.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </FilterSelect>
            <FilterSelect value={roadTypeFilter} onChange={(e) => setRoadTypeFilter(e.target.value)}>
              <option value="">All Roads</option>
              {uniqueRoadTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </FilterSelect>
          </SearchGroup>
          {filteredLocations.length > 0 && (
            <PaginationContainer>
              <PageButton onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>←</PageButton>
              {getPageNumbers().map((page, idx) => (
                typeof page === 'number' ? (
                  <PageButton key={idx} isActive={page === currentPage} onClick={() => goToPage(page)}>{page}</PageButton>
                ) : (
                  <PageInfo key={idx}>...</PageInfo>
                )
              ))}
              <PageButton onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>→</PageButton>
              <PerPageSelect value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={96}>96</option>
              </PerPageSelect>
            </PaginationContainer>
          )}
        </TopBar>

        {viewMode === 'cards' && paginatedLocations.length > 0 && (
          <CardsContainer>
            {paginatedLocations.map((loc) => (
              <Card key={loc._id}>
                <CardHeader>
                  <SrNoContainer>
                    <SrNo>#{loc.srNo || '-'}</SrNo>
                    <CopyButton 
                      onClick={() => handleCopySrNo(loc.srNo || '')} 
                      title={copiedId === loc.srNo ? 'Copied!' : 'Copy Sr.No'}
                    >
                      {copiedId === loc.srNo ? '✓' : <CopyIcon />}
                    </CopyButton>
                  </SrNoContainer>
                  <LocationName title={loc.location}>{loc.location || '-'}</LocationName>
                </CardHeader>

                <BadgesRow>
                  <Badge>{loc.ca || '-'}</Badge>
                  <Badge color="#3b82f6">{loc.area || '-'}</Badge>
                  <StretchBadge side={loc.stretchSide || ''}>{loc.stretchSide || '-'}</StretchBadge>
                  {loc.roadType && <Badge color="#8b5cf6">{loc.roadType}</Badge>}
                </BadgesRow>

                <RouteSection>
                  <RoutePoint>
                    <RouteLabel>From</RouteLabel>
                    <RouteName>{loc.from || '-'}</RouteName>
                    <RouteCoords>{loc.fromLatLong || '-'}</RouteCoords>
                  </RoutePoint>
                  <RouteConnector>
                    <ConnectorLine />
                    <DistanceLabel 
                      href={getGoogleMapsUrl(loc.fromLatLong, loc.toLatLong) || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View route on Google Maps"
                    >{calculateDistance(loc.fromLatLong, loc.toLatLong)}</DistanceLabel>
                  </RouteConnector>
                  <RoutePoint>
                    <RouteLabel>To</RouteLabel>
                    <RouteName>{loc.to || '-'}</RouteName>
                    <RouteCoords>{loc.toLatLong || '-'}</RouteCoords>
                  </RoutePoint>
                </RouteSection>

                <SpecsRow>
                  <SpecItem>
                    <SpecLabel>Frontage</SpecLabel>
                    <SpecValue>{loc.minPlotFrontageMeters || '-'}m</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Depth</SpecLabel>
                    <SpecValue>{loc.minPlotDepthMeters || '-'}m</SpecValue>
                  </SpecItem>
                  {loc.district && (
                    <SpecItem>
                      <SpecLabel>District</SpecLabel>
                      <SpecValue>{loc.district}</SpecValue>
                    </SpecItem>
                  )}
                </SpecsRow>
              </Card>
            ))}
          </CardsContainer>
        )}

        {viewMode === 'table' && filteredLocations.length > 0 && (
          <TableWrapper>
            <TableScrollContainer>
              <ExcelTable>
                <TableHeader>
                  <tr>
                    <Th>Sr.No</Th>
                    <Th>Location</Th>
                    <Th>CA</Th>
                    <Th>Area</Th>
                    <Th>Side</Th>
                    <Th>From</Th>
                    <Th>From Lat/Long</Th>
                    <Th>To</Th>
                    <Th>To Lat/Long</Th>
                    <Th>Distance</Th>
                    <Th>Road Type</Th>
                    <Th>Frontage (m)</Th>
                    <Th>Depth (m)</Th>
                    <Th>District</Th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {filteredLocations.map((loc) => (
                    <tr key={loc._id}>
                      <Td>{loc.srNo || '-'}</Td>
                      <Td>{loc.location || '-'}</Td>
                      <Td>{loc.ca || '-'}</Td>
                      <Td>{loc.area || '-'}</Td>
                      <Td style={{ color: loc.stretchSide === 'LHS' ? '#10b981' : loc.stretchSide === 'RHS' ? '#f59e0b' : '#6b7280', fontWeight: 600 }}>
                        {loc.stretchSide || '-'}
                      </Td>
                      <Td>{loc.from || '-'}</Td>
                      <Td style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{loc.fromLatLong || '-'}</Td>
                      <Td>{loc.to || '-'}</Td>
                      <Td style={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>{loc.toLatLong || '-'}</Td>
                      <Td>
                        <a 
                          href={getGoogleMapsUrl(loc.fromLatLong, loc.toLatLong) || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1e3a5f', fontWeight: 600, textDecoration: 'none' }}
                        >
                          {calculateDistance(loc.fromLatLong, loc.toLatLong)}
                        </a>
                      </Td>
                      <Td>{loc.roadType || '-'}</Td>
                      <Td>{loc.minPlotFrontageMeters || '-'}</Td>
                      <Td>{loc.minPlotDepthMeters || '-'}</Td>
                      <Td>{loc.district || '-'}</Td>
                    </tr>
                  ))}
                </TableBody>
              </ExcelTable>
            </TableScrollContainer>
          </TableWrapper>
        )}

        {filteredLocations.length === 0 && (
          <NoDataMessage>
            No locations found matching your criteria.
          </NoDataMessage>
        )}

        <Footer>
          <p>Data updated regularly. Contact administration for queries.</p>
        </Footer>
      </ContentWrapper>
      <PuneMapModal open={showPuneMapModal} onClose={() => setShowPuneMapModal(false)} />
    </PageContainer>
  );
};

export default AreaStretchInfo;
